import { useEffect, useState } from "react";
import { fetchBscPracticePdf } from "../api/client";

/** Tipo documento riconosciuto dalla classificazione BSC (enum del contratto). */
export type DocType = "bl" | "invoice" | "packing_list" | "ex1" | "other";

/**
 * Accordion PDF riusabile per il modulo BSC.
 *
 * Mostra un header clickabile con titolo + badge; all'apertura carica in lazy
 * (uno o più) PDF dalla route `/bsc/{id}/pdf/{filename}` e li renderizza in
 * `<iframe>` affiancati. Gestione blob URL + cleanup automatico.
 *
 * Riassunto dei comportamenti:
 * - Chiuso di default (l'operatore apre su richiesta → risparmio bandwidth).
 * - Lazy load: fetch solo alla prima apertura, poi cached in blobUrls.
 * - Footer opzionale (children) per testo OCR o metadata extra.
 * - Se `pdfFilenames` è vuoto → mostra placeholder "PDF non disponibile".
 */
export function BscPdfAccordion({
  practiceId,
  title,
  icon,
  headerColor = "var(--indaco-brand-primary)",
  headerBg = "var(--indaco-brand-primary-light)",
  pdfFilenames,
  emptyLabel = "Nessun PDF disponibile",
  defaultOpen = false,
  headerActions,
  children,
}: {
  practiceId: string;
  title: string;
  icon?: string;
  headerColor?: string;
  headerBg?: string;
  pdfFilenames: string[];
  emptyLabel?: string;
  defaultOpen?: boolean;
  /** Azioni extra nell'header, accanto al download (es. sostituisci documento) — sempre visibili, non solo ad accordion aperto. */
  headerActions?: React.ReactNode;
  children?: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [blobUrls, setBlobUrls] = useState<Record<string, string | null>>({});
  const [loadingPdf, setLoadingPdf] = useState<string | null>(null);
  const [pdfErrors, setPdfErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open || pdfFilenames.length === 0) return;
    let cancelled = false;
    const toLoad = pdfFilenames.filter((fn) => !(fn in blobUrls));
    if (toLoad.length === 0) return;
    setLoadingPdf(toLoad[0]);
    Promise.all(
      toLoad.map((fn) =>
        fetchBscPracticePdf(practiceId, fn)
          .then((blob) => ({ fn, url: URL.createObjectURL(blob), err: null as string | null }))
          .catch((e) => ({ fn, url: null, err: e instanceof Error ? e.message : "Errore" })),
      ),
    ).then((results) => {
      if (cancelled) {
        results.forEach((r) => { if (r.url) URL.revokeObjectURL(r.url); });
        return;
      }
      setBlobUrls((prev) => {
        const next = { ...prev };
        results.forEach((r) => { next[r.fn] = r.url; });
        return next;
      });
      setPdfErrors((prev) => {
        const next = { ...prev };
        results.forEach((r) => { if (r.err) next[r.fn] = r.err as string; });
        return next;
      });
      setLoadingPdf(null);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    return () => { Object.values(blobUrls).forEach((u) => { if (u) URL.revokeObjectURL(u); }); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const anyPdf = pdfFilenames.length > 0;
  const allLoaded = pdfFilenames.every((fn) => fn in blobUrls);

  return (
    <div className="card" style={{ padding: 0, overflow: "hidden" }}>
      <div
        onClick={() => setOpen(!open)}
        style={{
          display: "flex", alignItems: "center", gap: "var(--space-2)",
          padding: "var(--space-2) var(--space-3)", cursor: "pointer",
          background: open ? headerBg : "transparent",
          borderBottom: open ? "1px solid var(--border-color)" : "none",
        }}
      >
        <button
          className="btn btn-outline"
          onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
          style={{ padding: "2px 8px", minWidth: 0 }}
          title={open ? "Comprimi" : "Espandi"}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
            {open ? "expand_less" : "expand_more"}
          </span>
        </button>
        {icon && (
          <span className="material-symbols-outlined" style={{ fontSize: 20, color: headerColor }}>{icon}</span>
        )}
        <span className="fw-semibold text-sm" style={{ flex: 1 }}>{title}</span>
        {!anyPdf && <span className="badge badge-neutral text-xs">mancante</span>}
        {anyPdf && (
          <span className="badge badge-info text-xs">{pdfFilenames.length} PDF</span>
        )}
        {anyPdf && allLoaded && open && blobUrls[pdfFilenames[0]] && (
          <a
            href={blobUrls[pdfFilenames[0]]!}
            download={pdfFilenames[0]}
            onClick={(e) => e.stopPropagation()}
            className="btn btn-outline"
            style={{ padding: "2px 10px", textDecoration: "none" }}
            title="Scarica PDF"
          >
            <span className="material-symbols-outlined" style={{ fontSize: 18, verticalAlign: "middle" }}>download</span>
          </a>
        )}
        {headerActions && (
          <span onClick={(e) => e.stopPropagation()} style={{ display: "inline-flex" }}>
            {headerActions}
          </span>
        )}
      </div>

      {open && (
        <div>
          {anyPdf && (
            <div style={{ position: "relative", background: "var(--indaco-gray-100)" }}>
              {(loadingPdf || !allLoaded) && (
                <div className="loading-center" style={{ minHeight: 280 }}>
                  <div className="spinner spinner-lg" />
                </div>
              )}
              {allLoaded && pdfFilenames.map((fn) => (
                blobUrls[fn] ? (
                  <div key={fn}>
                    {pdfFilenames.length > 1 && (
                      <div style={{
                        padding: "4px var(--space-3)", fontSize: "var(--font-size-xs)",
                        color: "var(--indaco-gray-500)", background: "var(--indaco-gray-50)",
                        borderBottom: "1px solid var(--border-color)",
                      }}>
                        {fn}
                      </div>
                    )}
                    <iframe
                      src={`${blobUrls[fn]}#view=FitH&toolbar=1`}
                      title={fn}
                      style={{ width: "100%", height: 460, border: "none", display: "block" }}
                    />
                  </div>
                ) : pdfErrors[fn] ? (
                  <div key={fn} style={{ padding: "var(--space-3)", color: "var(--indaco-danger)", fontSize: "var(--font-size-xs)" }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 14, verticalAlign: "middle" }}>warning</span> {fn}: {pdfErrors[fn]}
                  </div>
                ) : null
              ))}
            </div>
          )}

          {children}

          {!anyPdf && !children && (
            <div style={{ padding: "var(--space-4)", textAlign: "center", color: "var(--indaco-gray-400)" }}>
              <span className="material-symbols-outlined" style={{ fontSize: 40 }}>picture_as_pdf</span>
              <div className="text-sm">{emptyLabel}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Restituisce i filename il cui doc_type corrisponde al tipo richiesto.
 *  Supporta sia il nuovo schema {name: {path, doc_type}} sia il vecchio
 *  {name: path_string} (compat backward — vecchio schema trattato come "other"). */
export function getPdfsForDoc(
  pdfFiles: Record<string, { path: string; doc_type: DocType } | string> | null | undefined,
  docType: DocType,
): string[] {
  if (!pdfFiles) return [];
  return Object.entries(pdfFiles)
    .filter(([, v]) => {
      if (typeof v === "string") return false;
      return v.doc_type === docType;
    })
    .map(([name]) => name);
}

/** Tutti i filename (qualunque doc_type). Usato quando non si conosce il tipo. */
export function getAllPdfFilenames(
  pdfFiles: Record<string, { path: string; doc_type: DocType } | string> | null | undefined,
): string[] {
  if (!pdfFiles) return [];
  return Object.keys(pdfFiles);
}
